import { Search } from '@mui/icons-material';
import { InputAdornment, TextField } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';

import BDHelmet from '@/components/BDHelmet';
import Pagination from '@/components/ui/Pagination/Pagination';
import DetailUser from '@/components/Modal/User/DetailUser';
import CreateUser from '@/components/Modal/User/CreateUser';

import user from '@/api/user';
// import usersData from '@/assets/data/Users.json';
import icAddNew from '@/assets/icons/add-new-user.png';

const { getUsersTemporary } = user();

export default function TempUsers() {
	const [search, setSearch] = useState('');
	const [newUser, setNewUser] = useState(false);
	const [selectedRow, setSelectedRow] = useState(false);
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

	const oC = useRef(null);

	const objFilters = useMemo(() => {
		const obj = { page_size: paginationModel.pageSize };
		if (search) {
			obj.search = search;
			obj.max_size = true;
		}
		return obj;
	}, [search, paginationModel]);

	const {
		data: d,
		isFetching,
		isFetched,
	} = useQuery({
		queryKey: ['users', paginationModel, search],
		queryFn: () => {
			const obj = objFilters;
			if (!search && paginationModel.page) obj.page = paginationModel.page + 1;
			if (paginationModel.page === 0 && 'page' in obj) delete obj.page;
			return getUsersTemporary(obj);
		},
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});

	const dataCount =
		useMemo(() => {
			if (isFetched && d?.data?.count) {
				oC.current = d.data.count;
				return d.data.count;
			}
		}, [isFetched, d, oC]) ||
		oC.current ||
		0;
	const dataList = useMemo(() => d?.data?.results, [d]) || [];

	const columns = [
		{
			field: 'id',
			headerName: 'Nr',
			filterable: false,
			sortable: true,
			renderCell: (index) => {
				const pM = paginationModel;
				const idx = index.api.getAllRowIds().indexOf(index.id) + 1
				return idx + pM.page * pM.pageSize;
			},
		},
		{
			field: 'username',
			headerName: 'Navn',
			headerAlign: 'center',
			align: 'center',
			flex: 1,
		},
		{
			field: 'email',
			headerName: 'E-post',
			headerAlign: 'center',
			align: 'left',
			flex: 1,
		},
		{
			field: 'phonenumber',
			headerName: 'Telefonnummer',
			headerAlign: 'center',
			align: 'center',
			flex: 1,
		},
		{
			field: 'modify_by',
			headerName: 'Oppdatert av',
			headerAlign: 'center',
			align: 'center',
		},
		{
			field: 'modify_date',
			headerName: 'Oppdatert dato',
			headerAlign: 'center',
			align: 'center',
		},
		{
			field: 'privilegeLevelID',
			headerName: 'Rolle',
			headerAlign: 'center',
			align: 'center',
		},
	];

	const onSaveUser = () => {};
	const onCreateUser = () => {};

	return (
		<>
			<BDHelmet title='Temporary Users' description='BDS Samferdsel temporary users' />
			<div className='container flex flex-col gap-4 pt-8 pb-8 mx-auto'>
				<div className='flex flex-col gap-4'>
					<div className='flex justify-between gap-4'>
						<TextField
							id='search'
							className='w-full input-search'
							placeholder='Søk'
							onChange={(e) => {
								setSearch(e.target.value);
								if (paginationModel.page !== 0) {
									setPaginationModel({ page: 0, pageSize: 10 })
								}
							}}
							size='small'
							InputProps={{
								startAdornment: (
									<InputAdornment position='start'>
										<Search style={{ color: 'black' }} />
									</InputAdornment>
								),
							}}
						/>
						<button
							onClick={() => setNewUser(true)}
							title='Registrer ny bruker'
							className='relative flex items-center gap-2 min-w-[208px] h-10 btn'
						>
							<div className='flex items-center justify-center w-5 h-5 bg-white rounded-full'>
								<img src={icAddNew} />
							</div>
							Registrer ny bruker
						</button>
					</div>
					<div>
						<span className='font-bold'>Totalt </span> <span className='text-bd-blue'>{dataCount}</span>{' '}
						<span className='text-bd-gLight'>brukere</span>
					</div>
					<div>
						<DataGrid
							autoHeight
							rows={dataList}
							loading={isFetching}
							columns={columns}
							rowsPerPageOptions={[10, 20, 50]}
							className='mx-auto lg:max-w-[1600px] w-full h-full mt-0'
							disableColumnFilter
							disableColumnSelector
							disableDensitySelector
							disableToolbarButton
							disableRowSelectionOnClick
							getRowId={(row) => row.userid}
							slots={{
								pagination: Pagination,
							}}
							getRowClassName={(params) => {
								let cellClass = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
								if (selectedRow?.userid === params.row.userid) {
									cellClass += ' font-bold border border-black';
								}
								return cellClass;
							}}
							slotProps={{
								loadingOverlay: {
									variant: 'skeleton',
									noRowsVariant: 'skeleton',
								},
								pagination: {
									page: paginationModel.page,
									pageSize: paginationModel.pageSize,
									rowCount: dataCount,
								},
							}}
							onCellClick={(params, event) => {
								if (event.target.type === 'checkbox' || ['svg', 'path'].includes(event.target.tagName)) return;
								setSelectedRow(params.row);
							}}
							initialState={{
								pagination: { paginationModel: { page: 0, pageSize: 10 } },
							}}
							rowCount={dataCount}
							paginationModel={paginationModel}
							paginationMode='server'
							onPaginationModelChange={(obj) => setPaginationModel(obj)}
						/>
					</div>
				</div>
				{!!selectedRow && <DetailUser open={!!selectedRow} onSave={onSaveUser} data={selectedRow} onClose={() => setSelectedRow(false)} />}
				{!!newUser && <CreateUser open={!!newUser} onSave={onCreateUser} onClose={() => setNewUser(false)} />}
			</div>
		</>
	);
}
